// TelcoTrack — čistá matematika zoom/pan pro lightbox fotek (bez DOM).

export const ZOOMABLE_PHOTO_MIN_SCALE = 1;
export const ZOOMABLE_PHOTO_MAX_SCALE = 6;
export const ZOOMABLE_PHOTO_STEP = 0.4;
export const ZOOMABLE_PHOTO_DOUBLE_CLICK_SCALE = 2.4;
export const ZOOMABLE_PHOTO_WHEEL_FACTOR = 1.14;

export interface ZoomablePhotoTransform {
  scale: number;
  x: number;
  y: number;
}

export function resetZoomTransform(): ZoomablePhotoTransform {
  return { scale: ZOOMABLE_PHOTO_MIN_SCALE, x: 0, y: 0 };
}

export function clampZoomScale(scale: number): number {
  return Math.min(ZOOMABLE_PHOTO_MAX_SCALE, Math.max(ZOOMABLE_PHOTO_MIN_SCALE, scale));
}

/** Zoom around a viewport point so the pixel under the cursor stays put. */
export function zoomTransformAtPoint(
  current: ZoomablePhotoTransform,
  nextScale: number,
  originX: number,
  originY: number,
): ZoomablePhotoTransform {
  const scale = clampZoomScale(nextScale);
  if (scale <= ZOOMABLE_PHOTO_MIN_SCALE) return resetZoomTransform();
  if (scale === current.scale) return current;
  const worldX = (originX - current.x) / current.scale;
  const worldY = (originY - current.y) / current.scale;
  return {
    scale,
    x: originX - worldX * scale,
    y: originY - worldY * scale,
  };
}

export function panZoomTransform(
  current: ZoomablePhotoTransform,
  dx: number,
  dy: number,
): ZoomablePhotoTransform {
  if (current.scale <= ZOOMABLE_PHOTO_MIN_SCALE) return current;
  return { scale: current.scale, x: current.x + dx, y: current.y + dy };
}

/** Normalize degrees to 0 / 90 / 180 / 270 for view-only lightbox rotation. */
export function normalizePhotoRotationDeg(degrees: number): number {
  const stepped = ((Math.round(degrees / 90) % 4) + 4) % 4;
  return stepped * 90;
}

export function rotatePhotoDeg(currentDeg: number, deltaDeg: number): number {
  return normalizePhotoRotationDeg(currentDeg + deltaDeg);
}

export function scaleFromWheelDelta(currentScale: number, deltaY: number): number {
  const factor = deltaY > 0 ? 1 / ZOOMABLE_PHOTO_WHEEL_FACTOR : ZOOMABLE_PHOTO_WHEEL_FACTOR;
  return clampZoomScale(currentScale * factor);
}

export function pointerDistance(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function pointerMidpoint(
  a: { x: number; y: number },
  b: { x: number; y: number },
): { x: number; y: number } {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
